import type { ReactNode } from "react";
import { Pencil } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import type { BriefSummary } from "@/lib/brief";
import { STYLE_PREFERENCES } from "@/lib/wizard-schemas";

import { ColorSwatchGrid } from "./color-swatch-grid";
import { LogoPreview } from "./logo-preview";
import {
  linksOrDash,
  listOrDash,
  preferredLabel,
  stringOrDash,
} from "./brief-markdown";

// Packages that show pages/tech preference
const SHOWS_PAGES = new Set(["LANDING_PAGE", "BASIC_MVP", "ADVANCED_MVP"]);
const SHOWS_TECH = new Set(["BASIC_MVP", "ADVANCED_MVP"]);

type Variant = "review" | "detail";
type RenderTarget = "customer" | "admin";

export type BriefDisplayProps = {
  summary: BriefSummary;
  variant?: Variant;
  renderTarget?: RenderTarget;
  onEditStep?: (step: number) => void;
};

export function BriefDisplay({
  summary,
  variant = "detail",
  renderTarget = "customer",
  onEditStep,
}: BriefDisplayProps) {
  const showEditButtons = variant === "review" && !!onEditStep;
  const styleLabel =
    STYLE_PREFERENCES.find((s) => s.value === summary.stylePreference)?.label || "—";
  const showPages = SHOWS_PAGES.has(summary.packageType);
  const showTech = SHOWS_TECH.has(summary.packageType);

  return (
    <div className="space-y-6">
      <Section title="Project basics" step={2} onEdit={showEditButtons ? onEditStep : undefined}>
        <Row label="Business name" value={stringOrDash(summary.businessName)} />
        <Row label="Project type" value={stringOrDash(summary.projectType)} />
        <Row label="Industry" value={stringOrDash(summary.industry)} />
        <Row label="Target audience" value={stringOrDash(summary.targetAudience)} wrap />
      </Section>

      <Section title="Requirements & content" step={3} onEdit={showEditButtons ? onEditStep : undefined}>
        <Row label="Description" value={summary.description} wrap />
        <Row label="Success criteria" value={stringOrDash(summary.successCriteria)} wrap />
        <Row label="Features" value={listOrDash(summary.features)} wrap />
        {showPages ? <Row label="Pages" value={listOrDash(summary.pages)} /> : null}
        <Row label="Reference links" value={linksOrDash(summary.references)} wrap />
        {showTech ? <Row label="Tech preference" value={stringOrDash(summary.techPreference)} /> : null}
      </Section>

      <Section title="Branding" step={4} onEdit={showEditButtons ? onEditStep : undefined}>
        {summary.packageType !== "BRAND_PACKAGE" ? (
          <Row label="Logo" value={<LogoPreview url={summary.logoUrl} />} />
        ) : null}
        <Row
          label="Colors"
          value={
            summary.brandColors.length > 0 ? (
              <ColorSwatchGrid colors={summary.brandColors} />
            ) : (
              stringOrDash(summary.brandVibe)
            )
          }
        />
        <Row label="Style" value={styleLabel} />
        <Row label="References" value={linksOrDash(summary.designReferences)} wrap />
        <Row label="Avoid" value={stringOrDash(summary.avoidList)} wrap />
      </Section>

      <Section title="Contact" step={5} onEdit={showEditButtons ? onEditStep : undefined}>
        <Row label="Name" value={stringOrDash(summary.customerName)} />
        <Row label="Email" value={stringOrDash(summary.customerEmail)} />
        <Row label="Phone" value={stringOrDash(summary.customerPhone)} />
        <Row label="Preferred" value={preferredLabel(summary.preferredContact)} />
        <Row label="Timezone" value={stringOrDash(summary.timezone)} />
        {renderTarget === "admin" ? null : null}
      </Section>
    </div>
  );
}

function Section({
  title,
  step,
  onEdit,
  children,
}: {
  title: string;
  step: number;
  onEdit?: (step: number) => void;
  children: ReactNode;
}) {
  return (
    <Card className="space-y-3 p-5">
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold">{title}</h3>
        {onEdit ? (
          <Button type="button" variant="ghost" size="sm" onClick={() => onEdit(step)}>
            <Pencil className="size-3.5" />
            Edit
          </Button>
        ) : null}
      </div>
      <dl className="grid gap-x-4 gap-y-3 text-sm sm:grid-cols-[10rem_1fr]">
        {children}
      </dl>
    </Card>
  );
}

function Row({ label, value, wrap }: { label: string; value: ReactNode; wrap?: boolean }) {
  return (
    <>
      <dt className="text-xs text-muted-foreground sm:pt-0.5">{label}</dt>
      <dd className={wrap ? "whitespace-pre-wrap text-sm" : "text-sm"}>{value}</dd>
    </>
  );
}
